import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useDebugSettings } from "@/hooks/admin/useDebugSettings";
import { useToast } from "@/hooks/use-toast";

interface AdminSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AdminSettingsDialog({ open, onOpenChange }: AdminSettingsDialogProps) {
  const { toast } = useToast();

  // Debug settings hook
  const { 
    data: debugSettings, 
    isLoading: isLoadingDebug,
    updateDebugSettings,
  } = useDebugSettings();

  const handleDebugToggle = (checked: boolean) => {
    updateDebugSettings(checked);
    toast({
      title: checked ? 'Debug Mode Enabled' : 'Debug Mode Disabled',
      description: checked ? 'Detailed backend logging has been enabled for 2 hours.' : 'Detailed backend logging has been disabled.'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Admin Settings</DialogTitle>
          <DialogDescription>
            Manage system-wide settings for the legislative review application.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Debug Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Debug Settings</CardTitle>
              <CardDescription>
                Enable detailed logging for troubleshooting backend issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Label htmlFor="debug-mode" className="text-base font-normal">
                  Enable Debug Mode
                </Label>
                <Switch
                  id="debug-mode"
                  checked={debugSettings?.enabled || false}
                  onCheckedChange={handleDebugToggle}
                  disabled={isLoadingDebug}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {debugSettings?.enabled 
                  ? `Last updated by ${debugSettings.updated_by} at ${new Date(debugSettings.updated_at || '').toLocaleString()}`
                  : 'Debug mode is currently disabled'}
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}